import { useTranslation } from "@coworker/locales";
import React, { useState, useCallback, Dispatch, SetStateAction } from "react";
import styled from "styled-components";
import { HFBList } from "./HFBList";
import { DivisionComponent } from "../../InputPopup/UserFiltersRow/Divisions/DivisionComponent";
import { useStoreId } from "../../../core/auth/useLoggedInUser";
import FixaSearch from "@coworker/apprestructured/src/shared/wrappers/FixaSearch/FixaSearch";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import styles from "@coworker/apprestructured/src/shared/styles/common-styles.module.css";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { FixaPrompt } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaPrompt";
import { FixaModalFooter } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalFooter";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";

const SearchContainer = styled("div")`
  padding: 24px 16px;
  border-bottom: 1px solid var(--grey200);
`;

const FooterContainer = styled("div")`
  padding: 16px;
  border-top: 1px solid var(--grey200);
`;

interface PopupProps {
  openAreaSearchListPopup: boolean;
  setOpenAreaSearchListPopup: React.Dispatch<SetStateAction<boolean>>;
  onSubmit: (area: string | { type: string; item: string } | string[]) => void;
  isAcesAndKing: boolean;
  area: string;
  setArea: Dispatch<SetStateAction<string>>;
  multiSelect?: boolean;
  selectedAreas?: string[];
  maxSelections?: number;
}

export function AreaSearchListModal({
  onSubmit,
  isAcesAndKing,
  area,
  setArea,
  openAreaSearchListPopup,
  setOpenAreaSearchListPopup,
  multiSelect = false,
  selectedAreas = [],
  maxSelections = 3,
}: PopupProps) {
  const [searchValue, setSearchValue] = useState<string>();
  const [localSelectedAreas, setLocalSelectedAreas] =
    useState<string[]>(selectedAreas);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const enableDivisionsArea = false;
  const store_id = useStoreId();

  const { t } = useTranslation();

  const onSelectedArea = useCallback(
    (area: string | { type: string; item: string }) => {
      const areaValue = typeof area === "string" ? area : area.item;
      onSubmit(areaValue);
    },
    [onSubmit]
  );

  const handleMultiSelectChange = useCallback((values: string[]) => {
    setLocalSelectedAreas(values);
  }, []);

  const handleConfirmMultiSelect = useCallback(() => {
    // Allow "whole store" as a single selection
    const hasWholeStore = localSelectedAreas.includes("whole=whole");

    if (hasWholeStore && localSelectedAreas.length > 1) {
      // This should not happen due to mutual exclusivity, but check anyway
      setShowErrorModal(true);
      return;
    }

    if (!hasWholeStore && localSelectedAreas.length > maxSelections) {
      setShowErrorModal(true);
      return;
    }

    onSubmit(localSelectedAreas);
    setOpenAreaSearchListPopup(false);
  }, [localSelectedAreas, onSubmit, setOpenAreaSearchListPopup, maxSelections]);

  const handleClose = useCallback(() => {
    setLocalSelectedAreas(selectedAreas);
    setOpenAreaSearchListPopup(false);
  }, [selectedAreas, setOpenAreaSearchListPopup]);

  const excessCount = localSelectedAreas.length - maxSelections;

  // Sync local state when modal opens
  const handleModalOpen = useCallback(() => {
    setLocalSelectedAreas(selectedAreas);
  }, [selectedAreas]);

  return (
    <>
      <FixaModal
        visible={openAreaSearchListPopup}
        handleCloseBtn={handleClose}
        onModalOpened={handleModalOpen}
      >
        <FixaSheets
          labelledById="testbuy-area-selection-list-modal-header"
          size="small"
          header={
            <FixaModalHeader
              id="testbuy-area-selection-list-modal-header"
              className={styles["modal-header-border"]}
              title={t("location3String")}
            />
          }
          footer={
            multiSelect ? (
              <FooterContainer>
                <FixaButton
                  type="primary"
                  text={t("applyString")}
                  onClick={handleConfirmMultiSelect}
                  disabled={localSelectedAreas.length === 0}
                  fluid
                />
              </FooterContainer>
            ) : null
          }
        >
          <div data-testid="number-of-articles-container">
            {enableDivisionsArea ? (
              <DivisionComponent
                storeId={store_id}
                setChecked={onSelectedArea}
              />
            ) : (
              <>
                <SearchContainer>
                  <FixaSearch
                    data-testid="search-input"
                    id="areaInput"
                    value={searchValue ?? ""}
                    placeholder={t("searchBasicString")}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onClear={() => setSearchValue("")}
                  />
                </SearchContainer>
                <HFBList
                  filter={searchValue}
                  onSelect={onSelectedArea}
                  isAcesAndKing={isAcesAndKing}
                  area={area}
                  setArea={setArea}
                  multiSelect={multiSelect}
                  selectedAreas={localSelectedAreas}
                  onMultiSelect={handleMultiSelectChange}
                  maxSelections={maxSelections}
                />
              </>
            )}
          </div>
        </FixaSheets>
      </FixaModal>
      <FixaModal
        visible={showErrorModal}
        handleCloseBtn={() => setShowErrorModal(false)}
      >
        <FixaPrompt
          title={t("maxHfbSelectionInfoString", { max: maxSelections })}
          titleId="hfb-selection-error-modal"
          header={
            <FixaModalHeader title={""} ariaCloseTxt={t("closeString")} />
          }
          footer={
            <FixaModalFooter>
              <FixaButton
                text={t("OkString")}
                type="primary"
                onClick={() => setShowErrorModal(false)}
              />
            </FixaModalFooter>
          }
        >
          <></>
        </FixaPrompt>
      </FixaModal>
    </>
  );
}
